import type { SubmitAnswerItem } from './local-api';

export type PendingSubmission = {
  id: string;
  testSessionId: string;
  answers: SubmitAnswerItem[];
  token: string;
  timestamp: number;
  retryCount: number;
};

const STORAGE_KEY = 'pending-submissions';

const PENDING_CHANGED = 'pending-submissions-changed';

function load(): PendingSubmission[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingSubmission[]) : [];
  } catch {
    return [];
  }
}

function persist(items: PendingSubmission[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
  window.dispatchEvent(new CustomEvent(PENDING_CHANGED));
}

export function addPendingSubmission(
  testSessionId: string,
  answers: SubmitAnswerItem[],
  token: string
): void {
  const existing = load();
  // Avoid duplicates for the same session
  if (existing.some((s) => s.testSessionId === testSessionId)) return;
  existing.push({
    id: `${testSessionId}-${Date.now()}`,
    testSessionId,
    answers,
    token,
    timestamp: Date.now(),
    retryCount: 0,
  });
  persist(existing);
}

export function removePendingSubmission(id: string): void {
  persist(load().filter((s) => s.id !== id));
}

export function incrementRetryCount(id: string): void {
  const items = load();
  const entry = items.find((s) => s.id === id);
  if (entry) {
    entry.retryCount += 1;
    persist(items);
  }
}

export function getPendingSubmissions(): PendingSubmission[] {
  return load();
}

export function getPendingCount(): number {
  return load().length;
}

export function onPendingChanged(cb: () => void): () => void {
  window.addEventListener(PENDING_CHANGED, cb);
  return () => window.removeEventListener(PENDING_CHANGED, cb);
}
